import { resolvePage, type PageResolver } from "./page-registry"
import {
  FetchPage,
  RequestAdapter,
  ResponseAdapter,
  VisitMethod,
} from "./types"

// The page map is an argument: by default the one the composition root
// registered at boot. Nothing here imports a page.
export function buildInertiaFetchPage(
  pages: PageResolver = resolvePage,
): FetchPage {
  const inertiaData = readInertiaPage()
  const requestAdapter = buildInertiaRequestAdapter(inertiaData.version)
  const responseAdapter = buildInertiaResponseAdapter(pages)

  return function fetchPage({ url, method, data }) {
    const controller = new AbortController()
    const { signal } = controller

    async function performFetch() {
      const response = await requestAdapter({ url, method, data, signal })
      const page = await responseAdapter({ response, signal })

      return page
    }

    return {
      promise: performFetch(),
      abort: () => controller.abort(),
      cancelled: false,
    }
  }
}

export function buildInertiaResponseAdapter(
  pages: PageResolver,
): ResponseAdapter {
  return async ({
    response,
    signal,
  }: {
    response: Response
    signal: AbortSignal
  }) => {
    const { component, props } = await response.json()

    signal.throwIfAborted()

    const Component = pages(component).default
    return {
      name: component,
      node: <Component />,
      baseUrl: new URL(response.url),
      props,
      // The Inertia host shares transient page data as a prop of its own
      // beside the text flash; a response with none carries an empty bag.
      transient: props.transient ?? {},
    }
  }
}

export function readInertiaPage() {
  const inertiaPageEl = document.querySelector("[data-page='app']")

  if (inertiaPageEl === null) {
    throw new Error("Inertia page element not found")
  }

  return JSON.parse(inertiaPageEl.textContent!)
}

function readCsrfTokenMetaTag() {
  const metaEl = document.querySelector('meta[name="csrf-token"]')

  if (metaEl === null) {
    return null
  }

  return metaEl.getAttribute("content")!
}

export function buildInertiaRequestAdapter(version: string): RequestAdapter {
  return async function inertiaFetch({ url, method, data, signal }) {
    const requestInit: RequestInit & { headers: Headers } = {
      headers: new Headers(),
      method,
      signal,
    }

    requestInit.headers.set("X-Inertia", "true")
    requestInit.headers.set("X-Inertia-Version", version)
    requestInit.headers.set("Accept", "text/html, application/xhtml+xml")

    if (method === VisitMethod.GET || method === VisitMethod.DELETE) {
      for (const [key, value] of Object.entries(data)) {
        url.searchParams.append(key, String(value))
      }
    }

    if (
      method === VisitMethod.POST ||
      method === VisitMethod.PUT ||
      method === VisitMethod.PATCH
    ) {
      requestInit.headers.set("Content-Type", "application/json")

      const csrfToken = readCsrfTokenMetaTag()
      if (csrfToken) {
        requestInit.headers.set("X-Xsrf-Token", csrfToken)
      }

      requestInit.body = JSON.stringify(data)
    }

    return await fetch(url, requestInit)
  }
}
